import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { loadProjectList } from "@/lib/content";
import { Rise } from "@/components/motion/Reveal";

const SPANS = ["md:col-span-7", "md:col-span-5", "md:col-span-4", "md:col-span-4", "md:col-span-4"];

export default function OurWork() {
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    loadProjectList().then((list) => setProjects(list.slice(0, 5))).catch(() => setProjects([]));
  }, []);

  if (!projects.length) return null;

  return (
    <section
      id="work"
      data-testid="work-section"
      className="py-24 lg:py-32 border-t border-[#262626]"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-16">
        <Rise>
        <div className="grid md:grid-cols-12 gap-8 items-end mb-14">
          <div className="md:col-span-7">
            <div className="text-xs tracking-[0.24em] uppercase text-white/60 mb-4">
              — Portfolio
            </div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-bold leading-[0.95]">
              Recent work.
              <br />
              <span className="text-white/40">Real jobs, real results.</span>
            </h2>
          </div>
          <div className="md:col-span-5">
            <p className="text-base md:text-lg text-white/70 leading-relaxed">
              Every project completed to BC Electrical Code with permits pulled
              and inspections passed. A small sample of what we&rsquo;ve wired up
              recently across the Lower Mainland.
            </p>
            <Link to="/projects" className="mt-4 inline-flex items-center gap-2 text-[#E60000] font-bold text-sm tracking-wide">
              View all case studies <ArrowUpRight className="w-4 h-4" strokeWidth={2.5} />
            </Link>
          </div>
        </div>
        </Rise>

        <motion.div
          className="grid md:grid-cols-12 gap-4 md:gap-6"
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-60px" }}
          variants={{ hidden: {}, show: { transition: { staggerChildren: 0.08 } } }}
        >
          {projects.map((w, i) => (
            <motion.div
              key={w.slug}
              className={SPANS[i] || "md:col-span-4"}
              variants={{
                hidden: { opacity: 0, y: 24 },
                show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
              }}
            >
              <Link
                to={`/projects/${w.slug}`}
                data-testid={`work-card-${i}`}
                className="group relative overflow-hidden border border-[#262626] hover:border-white/40 transition-colors block h-full"
              >
                <div className="aspect-[4/3] md:aspect-[5/4] overflow-hidden bg-[#141414]">
                  {w.featureImage && (
                    <img
                      src={w.featureImage}
                      alt={`${w.title} — ${w.location || ""}`}
                      loading="lazy"
                      className="w-full h-full object-cover grayscale-[10%] group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700"
                    />
                  )}
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/40 to-transparent pointer-events-none" />
                <div className="absolute inset-0 flex flex-col justify-end p-5 md:p-6">
                  <div className="text-[10px] tracking-[0.24em] uppercase text-[#E60000] font-bold mb-1">
                    {w.serviceType}
                  </div>
                  <div className="flex items-end justify-between gap-3">
                    <div>
                      <div className="font-display text-xl md:text-2xl tracking-tight font-bold leading-tight">
                        {w.title}
                      </div>
                      <div className="text-xs text-white/60 mt-1">{w.location}</div>
                    </div>
                    <ArrowUpRight
                      className="w-5 h-5 opacity-0 -translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 transition-all"
                      strokeWidth={2.5}
                    />
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
